<template>
  <Field
    v-slot="{ errors, field }"
    v-model="selected"
    :required="isRequired"
    :name="name"
    :rules="validationTypedSchema"
  >
    <label
      :class="[{ error: hasError(errors), disabled: disabled }, cssClass || '']"
      tabindex="0"
    >
      <slot name="label" />
      <slot name="label-status">
        <span v-if="isRequired" class="label-status">*</span>
        <span v-else class="label-help">(optional)</span>
      </slot>
      <div class="select">
        <span class="caret"><span class="l" /><span class="r" /></span>
        <select
          v-model="selected"
          :required="isRequired"
          :name="name"
          :disabled="disabled"
          v-bind="{ ...$attrs, ...field }"
          @change:modelValue="$emit('change', $event.target.value)"
        >
          <slot>
            <option v-if="defaultSelectName" selected="selected" value="">
              {{ defaultSelectName }}
            </option>
            <option
              v-for="(option, index) in selectOptions"
              :key="index"
              :value="option.value"
              :disabled="option.disabled"
            >
              {{ option.name }}
            </option>
          </slot>
        </select>
      </div>
    </label>
    <input-error-message :has-error="hasError(errors)">
      <slot
        v-if="firstFailedRule(errors)"
        name="errorMessage"
        :failed-rules="errors"
      >
        {{
          $t(`failedRules.${firstFailedRule(errors)}`, {
            label: labelText,
            ...validationRules,
          })
        }}
      </slot>
      <template v-else>
        {{ errors[0] }}
      </template>
    </input-error-message>
    <p v-if="$slots.helpText" class="help-text">
      <slot name="helpText" />
    </p>
  </Field>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Watch,
  Vue,
  toNative,
} from '~/utility/pu-class-decorator'
import BaseInput from './BaseInput.vue'
import { Field } from 'vee-validate'

export interface SelectOption {
  name: string
  value: string
  selected?: boolean
  disabled?: boolean
}

@Component({
  mixins: [BaseInput],
  components: { Field },
  emits: ['change', 'input'],
})
class BaseSelect extends Vue {
  @Prop()
  selectOptions!: SelectOption[]

  @Prop()
  defaultSelectName!: string

  @Prop({ required: false })
  cssClass?: string

  // TODO: selected should be a prop really as can only have one
  selected: string | null = null

  @Watch('selected')
  selectedChanged(_, oldSelected) {
    // only emit if changed
    if (oldSelected !== null) {
      this.$emit('change', {
        select: this.name,
        selectedValue: this.selected,
      })
    }
  }

  @Watch('selectOptions', { immediate: true })
  changeSelection() {
    const selected = this.selectOptions.find((o) => o.selected)
    this.selected = selected ? selected.value : ''
  }

  get listeners() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { change, ...listeners } = this.$attrs
    return listeners
  }
}

export default toNative(BaseSelect)
</script>

<style scoped>
.select {
  position: relative;
  margin-left: 0.25em;
  display: inline-block;
}

.asblock .select {
  display: block;
  margin: 0.25em 0 0 0;
}

.select select {
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  background: white;
  border-radius: 0.2em;
  border: 1px solid var(--colour-light-grey);
  color: var(--colour-text-default);
  cursor: pointer;
  display: inline-block;
  font-size: 1em;
  margin-top: 0.15em;
  min-height: 2em;
  outline: 0;
  padding: 0.4em 2.5em 0.4em 1em;
  pointer-events: auto;
  width: 100%;
}

select:disabled {
  opacity: 0.5;
  pointer-events: none;
}

:deep(.label-help) {
  margin-left: 0.25em;
  color: var(--colour-text-light);
}

.caret {
  display: flex;
  align-items: center;
  width: 1.7em;
  height: 1.7em;
  flex-shrink: 0;
  font-size: 0.65em;
  position: absolute;
  top: 30%;
  right: 1em;
  pointer-events: none;
}

.caret span {
  display: block;
  border-radius: 0.2em;
  height: 0.2em;
  width: 100%;
  border: 0.1em solid var(--colour-text-light);
  background: var(--colour-text-light);
}

.caret span.l {
  transform: rotate(45deg) translate(0.25em, 0);
}

.caret span.r {
  transform: rotate(-45deg) translate(-0.25em, 0);
}

.disabled .caret span {
  border-color: #ddd;
}
</style>
